import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, Sparkles, Info, ChevronDown } from 'lucide-react';
import { getCategories } from '../../services/firestoreService';
import './CustomScrollbar.css';
import './ModalAnimations.css';

function AIQuizBuilder({ isOpen, onClose }) {
  const [prompt, setPrompt] = useState('');
  const [questionTypes, setQuestionTypes] = useState({
    singleChoice: true,
    multipleChoice: false,
    trueFalse: false,
    shortAnswer: false
  });
  const [totalQuestions, setTotalQuestions] = useState(5);
  const [allCategories, setAllCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [categorySearch, setCategorySearch] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const [showTooltip, setShowTooltip] = useState(false);
  const categoryTriggerRef = useRef(null);

  const maxChars = 500;

  // Load categories
  useEffect(() => {
    const loadCategories = async () => {
      const result = await getCategories();
      if (result.success) {
        setAllCategories(result.data);
      }
    };
    if (isOpen) {
      loadCategories();
    }
  }, [isOpen]);

  // Lock body scroll when modal opens
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.height = '100vh';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.height = '';
    };
  }, [isOpen]);

  const handleQuestionTypeToggle = (type) => {
    setQuestionTypes(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const updateDropdownPosition = () => {
    if (categoryTriggerRef.current) {
      const rect = categoryTriggerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  };

  const handleCategoryToggle = () => {
    if (!showCategoryDropdown) {
      updateDropdownPosition();
    }
    setShowCategoryDropdown(!showCategoryDropdown);
  };

  const handleCategorySelect = (category) => {
    setSelectedCategoryId(category.id);
    setCategorySearch(category.name);
    setShowCategoryDropdown(false);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (categoryTriggerRef.current && !categoryTriggerRef.current.contains(e.target)) {
        // Check if click is inside the portal dropdown
        const dropdown = document.getElementById('category-dropdown-portal');
        if (dropdown && !dropdown.contains(e.target)) {
          setShowCategoryDropdown(false);
        }
      }
    };

    if (showCategoryDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('resize', updateDropdownPosition);
      window.addEventListener('scroll', updateDropdownPosition, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', updateDropdownPosition);
      window.removeEventListener('scroll', updateDropdownPosition, true);
    };
  }, [showCategoryDropdown]);

  const handleCreateQuiz = () => {
    if (!prompt.trim()) return;

    // TODO: Implement AI generation logic
    const selectedCategory = allCategories.find(c => c.id === selectedCategoryId);
    console.log('Generating quiz with:', {
      prompt,
      questionTypes,
      totalQuestions,
      categoryId: selectedCategoryId,
      categoryName: selectedCategory?.name
    });
  };

  const filteredCategories = allCategories.filter(cat =>
    cat.name.toLowerCase().includes(categorySearch.toLowerCase())
  );

  const selectedCategory = allCategories.find(c => c.id === selectedCategoryId);

  const isValid = prompt.trim().length > 0 &&
                  Object.values(questionTypes).some(v => v) &&
                  totalQuestions > 0 && totalQuestions <= 20 &&
                  selectedCategoryId !== '';

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 wizard-modal-overlay"
      style={{
        backgroundColor: 'rgba(15, 23, 42, 0.3)',
        backdropFilter: 'blur(8px)'
      }}
    >
      {/* AI Builder Modal Card */}
      <div
        className="w-full bg-white border border-[#E2E8F0] rounded-2xl shadow-2xl flex flex-col wizard-popup-card"
        style={{
          maxWidth: '900px',
          maxHeight: '85vh',
          height: 'auto',
          overflow: 'visible'
        }}
      >
        {/* Fixed Header */}
        <div className="relative h-16 flex items-center justify-center border-b border-[#E2E8F0] flex-shrink-0 px-6">
          {/* Back Button */}
          <button
            onClick={onClose}
            className="absolute left-6 top-1/2 -translate-y-1/2 transition-all"
            style={{
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#F1F5F9';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <ArrowLeft className="w-5 h-5 text-[#64748B]" />
          </button>

          {/* Title */}
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#6B7A8F]" />
            <h2 className="text-lg font-semibold text-[#0F172A]">Build with AI</h2>
          </div>

          {/* Badge */}
          <div
            className="absolute right-6 top-1/2 -translate-y-1/2 px-3 py-1 rounded-md text-xs font-bold tracking-wider"
            style={{
              backgroundColor: '#F1F3F5',
              color: '#6B7A8F'
            }}
          >
            BETA
          </div>
        </div>

        {/* Content */}
        <div className="px-8 py-5 popup-content-workspace" style={{
          overflow: 'visible'
        }}>
          {/* Split Column Grid */}
          <div className="ai-split-panel-grid" style={{
            display: 'grid',
            gridTemplateColumns: '1.4fr 1fr',
            gap: '32px',
            alignItems: 'start'
          }}>
            {/* Left Column: Prompt */}
            <div className="form-layout-group" style={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%'
            }}>
              <label className="form-layout-label" style={{
                fontSize: '14px',
                fontWeight: 600,
                color: '#0F172A',
                marginBottom: '8px'
              }}>
                Enter a topic or prompt <span className="text-[#EF4444]">*</span>
              </label>

              <div className="relative" style={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column'
              }}>
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value.slice(0, maxChars))}
                  placeholder="e.g., Create a 5-question multiple choice science quiz about electricity and basic circuits with intermediate difficulty questions."
                  className="ai-prompt-input-box"
                  style={{
                    width: '100%',
                    flexGrow: 1,
                    minHeight: '200px',
                    height: '200px',
                    backgroundColor: '#F8FAFC',
                    border: '1px solid #E2E8F0',
                    borderRadius: '12px',
                    padding: '16px',
                    paddingBottom: '32px',
                    fontSize: '15px',
                    color: '#0F172A',
                    outline: 'none',
                    resize: 'none',
                    transition: 'border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#10B981';
                    e.target.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
                    e.target.style.backgroundColor = '#FFFFFF';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#E2E8F0';
                    e.target.style.boxShadow = 'none';
                    if (!prompt) e.target.style.backgroundColor = '#F8FAFC';
                  }}
                />

                {/* Character Counter */}
                <div
                  className="absolute bottom-3 right-4 text-xs font-medium"
                  style={{
                    color: prompt.length >= maxChars ? '#EF4444' : '#94A3B8'
                  }}
                >
                  {prompt.length}/{maxChars}
                </div>
              </div>
            </div>

            {/* Right Column: Parameters in Structured Card */}
            <div className="ai-parameter-sidebar-card" style={{
              background: '#F8FAFC',
              border: '1px solid #E2E8F0',
              borderRadius: '12px',
              padding: '20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '14px',
              height: 'auto',
              minHeight: 'max-content',
              overflow: 'visible'
            }}>
              {/* Question Types */}
              <div className="form-layout-group">
                <label className="form-layout-label" style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#0F172A',
                  marginBottom: '10px',
                  display: 'block'
                }}>
                  Question Types <span className="text-[#EF4444]">*</span>
                </label>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label className="question-type-checkbox-item cursor-pointer px-2 py-1.5 rounded-lg transition-all" style={{
                    fontSize: '14px',
                    color: '#334155',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}
                  title="Single correct answer per question"
                  onMouseEnter={(e) => {
                    if (!questionTypes.singleChoice) {
                      e.currentTarget.style.backgroundColor = '#F8FAFC';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}>
                    <div style={{
                      position: 'relative',
                      width: '18px',
                      height: '18px',
                      minWidth: '18px',
                      minHeight: '18px',
                      border: questionTypes.singleChoice ? '1.5px solid #10B981' : '1.5px solid #CBD5E1',
                      borderRadius: '4px',
                      backgroundColor: '#FFFFFF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      boxShadow: questionTypes.singleChoice ? '0 0 0 3px rgba(16, 185, 129, 0.08)' : 'none'
                    }}
                    onMouseEnter={(e) => {
                      if (!questionTypes.singleChoice) {
                        e.currentTarget.style.borderColor = '#94A3B8';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!questionTypes.singleChoice) {
                        e.currentTarget.style.borderColor = '#CBD5E1';
                      }
                    }}>
                      <input
                        type="checkbox"
                        checked={questionTypes.singleChoice}
                        onChange={() => handleQuestionTypeToggle('singleChoice')}
                        style={{
                          position: 'absolute',
                          opacity: 0,
                          width: '100%',
                          height: '100%',
                          cursor: 'pointer',
                          margin: 0
                        }}
                      />
                      {questionTypes.singleChoice && (
                        <svg width="12" height="10" viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1 5L4.5 8.5L11 1.5" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </div>
                    <span className="font-medium" style={{ userSelect: 'none' }}>Single choice</span>
                  </label>

                  <label className="question-type-checkbox-item cursor-pointer px-2 py-1.5 rounded-lg transition-all" style={{
                    fontSize: '14px',
                    color: '#334155',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}
                  title="Multiple correct answers per question"
                  onMouseEnter={(e) => {
                    if (!questionTypes.multipleChoice) {
                      e.currentTarget.style.backgroundColor = '#F8FAFC';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}>
                    <div style={{
                      position: 'relative',
                      width: '18px',
                      height: '18px',
                      minWidth: '18px',
                      minHeight: '18px',
                      border: questionTypes.multipleChoice ? '1.5px solid #10B981' : '1.5px solid #CBD5E1',
                      borderRadius: '4px',
                      backgroundColor: '#FFFFFF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      boxShadow: questionTypes.multipleChoice ? '0 0 0 3px rgba(16, 185, 129, 0.08)' : 'none'
                    }}
                    onMouseEnter={(e) => {
                      if (!questionTypes.multipleChoice) {
                        e.currentTarget.style.borderColor = '#94A3B8';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!questionTypes.multipleChoice) {
                        e.currentTarget.style.borderColor = '#CBD5E1';
                      }
                    }}>
                      <input
                        type="checkbox"
                        checked={questionTypes.multipleChoice}
                        onChange={() => handleQuestionTypeToggle('multipleChoice')}
                        style={{
                          position: 'absolute',
                          opacity: 0,
                          width: '100%',
                          height: '100%',
                          cursor: 'pointer',
                          margin: 0
                        }}
                      />
                      {questionTypes.multipleChoice && (
                        <svg width="12" height="10" viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1 5L4.5 8.5L11 1.5" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </div>
                    <span className="font-medium" style={{ userSelect: 'none' }}>Multiple choice</span>
                  </label>

                  <label className="question-type-checkbox-item cursor-pointer px-2 py-1.5 rounded-lg transition-all" style={{
                    fontSize: '14px',
                    color: '#334155',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}
                  title="True or False questions"
                  onMouseEnter={(e) => {
                    if (!questionTypes.trueFalse) {
                      e.currentTarget.style.backgroundColor = '#F8FAFC';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}>
                    <div style={{
                      position: 'relative',
                      width: '18px',
                      height: '18px',
                      minWidth: '18px',
                      minHeight: '18px',
                      border: questionTypes.trueFalse ? '1.5px solid #10B981' : '1.5px solid #CBD5E1',
                      borderRadius: '4px',
                      backgroundColor: '#FFFFFF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      boxShadow: questionTypes.trueFalse ? '0 0 0 3px rgba(16, 185, 129, 0.08)' : 'none'
                    }}
                    onMouseEnter={(e) => {
                      if (!questionTypes.trueFalse) {
                        e.currentTarget.style.borderColor = '#94A3B8';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!questionTypes.trueFalse) {
                        e.currentTarget.style.borderColor = '#CBD5E1';
                      }
                    }}>
                      <input
                        type="checkbox"
                        checked={questionTypes.trueFalse}
                        onChange={() => handleQuestionTypeToggle('trueFalse')}
                        style={{
                          position: 'absolute',
                          opacity: 0,
                          width: '100%',
                          height: '100%',
                          cursor: 'pointer',
                          margin: 0
                        }}
                      />
                      {questionTypes.trueFalse && (
                        <svg width="12" height="10" viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1 5L4.5 8.5L11 1.5" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </div>
                    <span className="font-medium" style={{ userSelect: 'none' }}>True or false</span>
                  </label>

                  <label className="question-type-checkbox-item cursor-pointer px-2 py-1.5 rounded-lg transition-all" style={{
                    fontSize: '14px',
                    color: '#334155',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}
                  title="Open-ended text response questions"
                  onMouseEnter={(e) => {
                    if (!questionTypes.shortAnswer) {
                      e.currentTarget.style.backgroundColor = '#F8FAFC';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}>
                    <div style={{
                      position: 'relative',
                      width: '18px',
                      height: '18px',
                      minWidth: '18px',
                      minHeight: '18px',
                      border: questionTypes.shortAnswer ? '1.5px solid #10B981' : '1.5px solid #CBD5E1',
                      borderRadius: '4px',
                      backgroundColor: '#FFFFFF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      boxShadow: questionTypes.shortAnswer ? '0 0 0 3px rgba(16, 185, 129, 0.08)' : 'none'
                    }}
                    onMouseEnter={(e) => {
                      if (!questionTypes.shortAnswer) {
                        e.currentTarget.style.borderColor = '#94A3B8';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!questionTypes.shortAnswer) {
                        e.currentTarget.style.borderColor = '#CBD5E1';
                      }
                    }}>
                      <input
                        type="checkbox"
                        checked={questionTypes.shortAnswer}
                        onChange={() => handleQuestionTypeToggle('shortAnswer')}
                        style={{
                          position: 'absolute',
                          opacity: 0,
                          width: '100%',
                          height: '100%',
                          cursor: 'pointer',
                          margin: 0
                        }}
                      />
                      {questionTypes.shortAnswer && (
                        <svg width="12" height="10" viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1 5L4.5 8.5L11 1.5" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </div>
                    <span className="font-medium" style={{ userSelect: 'none' }}>Short answer</span>
                  </label>
                </div>
              </div>

              {/* Total Questions - Inline */}
              <div className="form-layout-group">
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: '0 0 auto' }}>
                    <label className="form-layout-label" style={{
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#0F172A',
                      margin: 0,
                      whiteSpace: 'nowrap'
                    }}>
                      Total Questions <span className="text-[#EF4444]">*</span>
                    </label>
                    <div
                      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
                      onMouseEnter={() => setShowTooltip(true)}
                      onMouseLeave={() => setShowTooltip(false)}
                    >
                      <Info
                        className="w-3.5 h-3.5"
                        style={{
                          color: '#94A3B8',
                          cursor: 'help',
                          transition: 'color 0.2s ease'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.color = '#64748B'}
                        onMouseLeave={(e) => e.currentTarget.style.color = '#94A3B8'}
                      />
                      {showTooltip && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            marginTop: '8px',
                            backgroundColor: '#0F172A',
                            color: '#FFFFFF',
                            fontSize: '12px',
                            fontWeight: 400,
                            lineHeight: '1.5',
                            padding: '8px 12px',
                            borderRadius: '8px',
                            whiteSpace: 'nowrap',
                            zIndex: 1000,
                            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.25)',
                            animation: 'tooltipFadeIn 0.2s ease-in',
                            pointerEvents: 'none'
                          }}
                        >
                          You can generate between 1 to 20 questions maximum.
                          <div
                            style={{
                              position: 'absolute',
                              top: '-5px',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              width: 0,
                              height: 0,
                              borderLeft: '5px solid transparent',
                              borderRight: '5px solid transparent',
                              borderBottom: '5px solid #0F172A'
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={totalQuestions}
                    onChange={(e) => setTotalQuestions(Math.min(20, Math.max(1, parseInt(e.target.value) || 1)))}
                    className="total-questions-input"
                    style={{
                      flex: 1,
                      height: '44px',
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px',
                      padding: '0 12px',
                      backgroundColor: '#FFFFFF',
                      fontSize: '14px',
                      color: '#0F172A',
                      outline: 'none',
                      transition: 'border-color 0.15s ease'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#10B981';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#E2E8F0';
                    }}
                  />
                </div>
              </div>

              {/* Category - Custom Dropdown with Portal */}
              <div className="form-layout-group">
                <label className="form-layout-label" style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#0F172A',
                  marginBottom: '8px',
                  display: 'block'
                }}>
                  Category <span className="text-[#EF4444]">*</span>
                </label>

                {/* Trigger Button */}
                <div
                  ref={categoryTriggerRef}
                  onClick={handleCategoryToggle}
                  style={{
                    width: '100%',
                    height: '44px',
                    border: showCategoryDropdown ? '1px solid #10B981' : '1px solid #E2E8F0',
                    borderRadius: '8px',
                    padding: '0 12px',
                    backgroundColor: '#FFFFFF',
                    fontSize: '14px',
                    color: selectedCategory ? '#0F172A' : '#94A3B8',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '8px',
                    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                    boxShadow: showCategoryDropdown ? '0 0 0 3px rgba(16, 185, 129, 0.1)' : 'none'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, overflow: 'hidden' }}>
                    {selectedCategory ? (
                      <>
                        <img
                          src={selectedCategory.image}
                          alt={selectedCategory.name}
                          style={{
                            width: '28px',
                            height: '28px',
                            borderRadius: '6px',
                            objectFit: 'cover',
                            flexShrink: 0
                          }}
                        />
                        <span style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {selectedCategory.name}
                        </span>
                      </>
                    ) : (
                      <span>Select a category...</span>
                    )}
                  </div>
                  <ChevronDown
                    size={16}
                    style={{
                      color: '#94A3B8',
                      flexShrink: 0,
                      transition: 'transform 0.2s ease',
                      transform: showCategoryDropdown ? 'rotate(180deg)' : 'rotate(0deg)'
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Responsive Styles & Tooltip Animation */}
          <style>
            {`
              @keyframes tooltipFadeIn {
                from {
                  opacity: 0;
                  transform: translateX(-50%) translateY(-4px);
                }
                to {
                  opacity: 1;
                  transform: translateX(-50%) translateY(0);
                }
              }

              @media (max-width: 840px) {
                .ai-split-panel-grid {
                  grid-template-columns: 1fr !important;
                  gap: 16px !important;
                }
              }
            `}
          </style>
        </div>

        {/* Fixed Footer */}
        <div className="wizard-footer-bar bg-white flex-shrink-0" style={{
          borderTop: '1px solid #E2E8F0',
          borderBottomLeftRadius: '16px',
          borderBottomRightRadius: '16px',
          padding: '20px 32px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <button
            onClick={onClose}
            className="secondary-cancel-action-btn transition-all"
            style={{
              backgroundColor: 'white',
              color: '#64748B',
              border: '2px solid #D1D5DB',
              fontWeight: 500,
              fontSize: '15px',
              padding: '10px 20px',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onMouseEnter={(e) => {
              e.target.style.borderColor = '#9CA3AF';
            }}
            onMouseLeave={(e) => {
              e.target.style.borderColor = '#D1D5DB';
            }}
          >
            Cancel
          </button>

          <button
            onClick={handleCreateQuiz}
            disabled={!isValid}
            className="primary-ai-action-btn transition-all"
            style={{
              backgroundColor: isValid ? '#10B981' : '#E2E8F0',
              color: isValid ? '#FFFFFF' : '#94A3B8',
              cursor: isValid ? 'pointer' : 'not-allowed',
              opacity: isValid ? 1 : 0.4,
              borderRadius: '10px',
              fontWeight: 600,
              fontSize: '15px',
              padding: '10px 24px',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onMouseEnter={(e) => {
              if (isValid) e.target.style.backgroundColor = '#059669';
            }}
            onMouseLeave={(e) => {
              if (isValid) e.target.style.backgroundColor = '#10B981';
            }}
          >
            Generate
          </button>
        </div>
      </div>

      {/* Category Dropdown Portal - Rendered outside modal */}
      {showCategoryDropdown && createPortal(
        <div
          id="category-dropdown-portal"
          style={{
            position: 'absolute',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: dropdownPosition.width,
            backgroundColor: '#FFFFFF',
            border: '1px solid #E2E8F0',
            borderRadius: '12px',
            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.15), 0 0 0 1px rgba(15, 23, 42, 0.05)',
            zIndex: 9999,
            maxHeight: '280px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          {/* Search Input */}
          <div style={{
            padding: '12px 12px 10px 12px',
            borderBottom: '1px solid #F1F5F9',
            flexShrink: 0
          }}>
            <input
              type="text"
              value={categorySearch}
              onChange={(e) => setCategorySearch(e.target.value)}
              placeholder="Search categories..."
              autoFocus
              style={{
                width: '100%',
                height: '40px',
                border: '1px solid #E2E8F0',
                borderRadius: '8px',
                padding: '0 14px',
                fontSize: '14px',
                outline: 'none',
                backgroundColor: '#FFFFFF',
                color: '#0F172A',
                transition: 'border-color 0.15s ease, box-shadow 0.15s ease'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#10B981';
                e.target.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.08)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#E2E8F0';
                e.target.style.boxShadow = 'none';
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {/* Category List with custom scrollbar */}
          <div
            className="custom-scrollbar"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
              padding: '8px 8px',
              overflowY: 'auto',
              flex: 1
            }}
          >
            {filteredCategories.length > 0 ? (
              filteredCategories.map((category) => (
                <div
                  key={category.id}
                  onClick={() => handleCategorySelect(category)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 12px',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    backgroundColor: selectedCategoryId === category.id ? '#F0FDF4' : 'transparent',
                    border: selectedCategoryId === category.id ? '1px solid #BBF7D0' : '1px solid transparent',
                    transition: 'all 0.15s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedCategoryId !== category.id) {
                      e.currentTarget.style.backgroundColor = '#F8FAFC';
                      e.currentTarget.style.borderColor = '#E2E8F0';
                    } else {
                      e.currentTarget.style.backgroundColor = '#DCFCE7';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedCategoryId !== category.id) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.borderColor = 'transparent';
                    } else {
                      e.currentTarget.style.backgroundColor = '#F0FDF4';
                      e.currentTarget.style.borderColor = '#BBF7D0';
                    }
                  }}
                >
                  <img
                    src={category.image}
                    alt={category.name}
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      objectFit: 'cover',
                      flexShrink: 0,
                      border: '1px solid rgba(226, 232, 240, 0.6)'
                    }}
                  />
                  <span
                    style={{
                      fontSize: '14px',
                      fontWeight: selectedCategoryId === category.id ? 600 : 500,
                      color: selectedCategoryId === category.id ? '#10B981' : '#0F172A',
                      flex: 1
                    }}
                  >
                    {category.name}
                  </span>
                </div>
              ))
            ) : (
              <div
                style={{
                  padding: '32px 20px',
                  textAlign: 'center',
                  fontSize: '14px',
                  color: '#94A3B8'
                }}
              >
                No categories found
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default AIQuizBuilder;
